import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Input, Textarea, Button } from '../components/primitives';
import { Mail, MapPin, Clock, MessageSquare } from 'lucide-react';
import { trackConversion } from '../utils/analytics';

const ContactForm = ({
  onSuccess,
  subjectPlaceholder = "What's this about?",
  subjectDefault,
  headingLevel = 'h2',
  className = '',
  showInfo = true,
  email = 'info@utopiancreations.one',
  location = 'San Francisco Bay Area, California',
  locationSub = 'Serving clients worldwide',
  hours = 'Texts get answered fastest, usually within the hour during the day. Email within a few hours.',
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  // Render time, sent as `_t` (ms to fill the form) so the endpoint's spam
  // guard can flag submits no human could type that fast.
  const mountedAt = useRef(0);
  useEffect(() => { mountedAt.current = Date.now(); }, []);
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
    delay: 200,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSent(false);

    try {
      const form = e.currentTarget;
      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());

      const response = await fetch('/api/forms/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...data,
          form_type: 'contact',
          source_site: 'utopian-creations-website',
          _t: Date.now() - mountedAt.current,
        }),
      });

      const result = await response.json();
      if (!result.ok || result.error) {
        throw new Error(result.error || 'Submission failed');
      }

      setSent(true);
      trackConversion('contact_submitted', { category: 'contact' });
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Contact submit failed:', err);
      setError('There was an error submitting your message. Please try again.');
      setSubmitting(false);
    }
  };

  const Heading = headingLevel;

  return (
    <section
      ref={ref}
      id="contact"
      className={`min-h-screen py-16 px-4 sm:px-6 lg:px-8 relative
                 transition-all duration-1000 ease-out transform
                 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
                 ${className}`}
    >
      <div className="container mx-auto max-w-5xl">
        <Heading className={`brand-header text-3xl sm:text-4xl font-bold font-sans text-center mb-12 transition-opacity duration-1000 ${inView ? 'opacity-100' : 'opacity-0'}`}>
          Get in touch
        </Heading>

        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Contact Form */}
          <div className="transition-all duration-1000 delay-300 transform">
            {sent && (
              <div className="mb-4 rounded-md bg-green-50 border border-green-200 p-3 text.success text-sm" role="status" aria-live="polite">
                Message sent! Redirecting…
              </div>
            )}
            {error && (
              <div className="mb-4 rounded-md bg-red-50 border border-red-200 p-3 text.danger text-sm" role="alert">
                {error}
              </div>
            )}
            <form
              onSubmit={handleSubmit}
              className={`p-6 rounded-lg shadow-lg 
                      bg-white border border-gray-200
                      transition-all duration-500`}
            >
              <Input
                label="Your Name"
                name="name"
                id="name"
                required
              />
              <Input
                label="Your Email"
                name="email"
                id="email"
                type="email"
                required
              />
              <Input
                label="Phone (optional)"
                name="phone"
                id="phone"
                type="tel"
              />
              <Input
                label="Subject"
                name="subject"
                id="subject"
                placeholder={subjectPlaceholder}
                defaultValue={subjectDefault}
              />
              <Textarea
                label="Message"
                name="message"
                id="message"
                rows={5}
                required
              />
              {/* Honeypot — bots fill it, people never see it. */}
              <div className="hidden" aria-hidden="true">
                <label>
                  Website
                  <input name="company_website" tabIndex={-1} autoComplete="off" />
                </label>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={submitting}
                className="w-full mt-2"
              >
                {submitting ? 'Sending…' : 'Send Message'}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          {showInfo && (
            <div className={`space-y-6 transition-all duration-1000 delay-500 transform ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text.strong">
                  <h3 className="brand-subheader text-lg font-medium font-sans">Text me</h3>
                  <p className="mt-1">
                    <a href="sms:+15106299022?body=Hi%20Josh%2C%20I%20want%20a%20Same-Day%20Launch.%20My%20business%20is%3A%20" className="font-semibold text-purple-600 hover:underline">(510) 629-9022</a>
                  </p>
                  <p className="mt-1 text-sm text.muted">Fastest way to reach me. Want a site today? Text &quot;same day&quot; and your business name.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Mail className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text.strong">
                  <h3 className="brand-subheader text-lg font-medium font-sans">Email</h3>
                  <p className="mt-1">{email}</p>
                  <p className="mt-1 text-sm text.muted">For anything longer than a text</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text.strong">
                  <h3 className="brand-subheader text-lg font-medium font-sans">Location</h3>
                  <p className="mt-1">{location}</p>
                  <p className="mt-1 text-sm text.muted">{locationSub}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Clock className="h-6 w-6 text-purple-600" />
                </div>
                <div className="text.strong">
                  <h3 className="brand-subheader text-lg font-medium font-sans">Business Hours</h3>
                  <p className="mt-1">{hours}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
